import React, { useState, useEffect, useCallback } from "react";
import Button from "../../../components/common/Button";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MdArrowBack,
  MdEdit,
  MdDelete,
  MdPeople,
  MdPerson,
  MdCheckCircle,
  MdCancel,
} from "react-icons/md";
import Card from "components/card";
import { Challenge, ChallengeUser } from "../../../types/challenge";
import { ChallengeService } from "../../../services/challengeService";
import ChallengeModal from "../../../components/challenges/ChallengeModal";
import UserAssignmentModal from "../../../components/challenges/UserAssignmentModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import Pagination from "../../../components/common/Pagination";
import { UpdateChallengeInput } from "../../../types/challenge";
import { ProfileImage } from "../../../types/user";
import LoadingSpinner from "components/common/LoadingSpinner";

const ChallengeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<ChallengeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit, setLimit] = useState(15);

  const challengeId = id && !isNaN(parseInt(id)) ? parseInt(id) : null;

  const fetchChallengeData = useCallback(async () => {
    if (!challengeId) return;

    setLoading(true);
    setError(null);

    try {
      const [challengeData, usersData] = await Promise.all([
        ChallengeService.getChallengeById(challengeId),
        ChallengeService.getChallengeUsers(challengeId, currentPage),
      ]);

      if (challengeData) {
        setChallenge(challengeData);
      } else {
        setError("Challenge not found");
      }

      if (usersData) {
        setAssignedUsers(usersData.users);
        setTotalUsers(usersData.total);
        setLimit(usersData.limit);
      }
    } catch (err: any) {
      setError("Failed to load challenge details");
    } finally {
      setLoading(false);
    }
  }, [challengeId, currentPage]);

  // Fetch challenge details
  useEffect(() => {
    if (challengeId) {
      fetchChallengeData();
    }
  }, [challengeId, fetchChallengeData]);

  const handleBack = () => {
    navigate("/admin/challenges");
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleManageUsers = () => {
    setShowUserAssignmentModal(true);
  };

  const confirmDeleteChallenge = async () => {
    if (!challenge) return;

    setOperationLoading(true);
    setOperationError(null);

    try {
      const result = await ChallengeService.deleteChallenge(challenge.id);

      if (result.success) {
        navigate("/admin/challenges");
      } else {
        setOperationError(result.error || "Failed to delete challenge");
      }
    } catch (err: any) {
      setOperationError(err.message || "An unexpected error occurred");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUpdateSubmit = async (challengeData: any) => {
    if (!challenge) return;

    setOperationLoading(true);
    setOperationError(null);

    try {
      const updateData: UpdateChallengeInput = {
        name: challengeData.name,
        conducted_at_year: challengeData.conducted_at_year,
        is_active: challengeData.is_active,
      };

      const result = await ChallengeService.updateChallenge(
        challenge.id,
        updateData
      );

      if (result.success && result.data) {
        setChallenge(result.data);
        setShowEditModal(false);
      } else {
        setOperationError(result.error || "Failed to update challenge");
      }
    } catch (err: any) {
      setOperationError(err.message || "An unexpected error occurred");
    } finally {
      setOperationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner bare />
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="p-6">
        <button
          onClick={handleBack}
          className="mb-4 flex items-center text-gray-600 hover:text-biafit-primary dark:text-gray-400 dark:hover:text-biafit-secondary"
        >
          <MdArrowBack className="mr-2 h-5 w-5" />
          Back to Challenges
        </button>
        <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900">
          <div className="text-red-800 dark:text-red-200">
            {error || "Challenge not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Actions */}
      <div className="flex items-end justify-between">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Back to Challenges</span>
        </button>

        <div className="mt-3 flex flex-wrap justify-end gap-3">
          <Button onClick={handleEdit} disabled={operationLoading}>
            <MdEdit className="mr-2 h-4 w-4" />
            Edit Challenge
          </Button>
          <button
            onClick={handleDelete}
            disabled={operationLoading}
            className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            <MdDelete className="mr-2 h-4 w-4" />
            Delete Challenge
          </button>
        </div>
      </div>

      {/* Error Message */}
      {operationError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900">
          <div className="text-red-800 dark:text-red-200">
            Error: {operationError}
          </div>
        </div>
      )}

      {/* Challenge Details Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {challenge.name}
          </h1>
          {challenge.is_active ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-100">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">
              Inactive
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Year</p>
            <p className="text-base font-medium text-navy-700 dark:text-white">
              {challenge.conducted_at_year}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Users
            </p>
            <p className="text-base font-medium text-navy-700 dark:text-white">
              {totalUsers}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
            <p className="text-base font-medium text-navy-700 dark:text-white">
              {new Date(challenge.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </Card>

      {/* Assigned Users Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Assigned Users ({totalUsers})
          </h2>
          <Button onClick={handleManageUsers}>
            <MdPeople className="mr-2 h-4 w-4" />
            Manage Users
          </Button>
        </div>

        {assignedUsers.length === 0 ? (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            No users assigned yet
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {assignedUsers.map((user) => {
                const fullName = `${user.first_name} ${user.last_name}`.trim();
                const profileImage: ProfileImage | null =
                  typeof user.profile_image === "string"
                    ? null
                    : user.profile_image;

                return (
                  <div
                    key={user.id}
                    className="flex cursor-pointer flex-col items-start justify-between rounded-md border border-gray-200 p-3 transition-colors hover:border-biafit-primary dark:border-gray-700 dark:hover:border-biafit-primary sm:flex-row sm:items-center"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                  >
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {profileImage?.full_path ? (
                          <img
                            src={profileImage.full_path}
                            alt={fullName || user.email}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-biafit-primary text-white">
                            <MdPerson className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                          {fullName || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="pt-3 text-left text-xs text-gray-700 dark:text-gray-400 sm:pt-0 sm:text-right">
                      Joined Challenge:{" "}
                      <span className="hidden sm:inline">
                        <br />
                      </span>
                      {new Date(user.pivot.created_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>

            <Pagination
              page={currentPage}
              limit={limit}
              total={totalUsers}
              totalPages={Math.ceil(totalUsers / limit)}
              entityName="users"
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </>
        )}
      </Card>

      {/* Edit Modal */}
      <ChallengeModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateSubmit}
        challenge={challenge}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      {/* User Assignment Modal */}
      <UserAssignmentModal
        isOpen={showUserAssignmentModal}
        onClose={() => setShowUserAssignmentModal(false)}
        challengeId={challenge.id}
        challengeName={challenge.name}
        onUpdate={fetchChallengeData}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteChallenge}
        title="Delete Challenge"
        message="Are you sure you want to delete this challenge? This action cannot be undone."
        itemName={challenge.name}
        loading={operationLoading}
        error={operationError}
      />
    </div>
  );
};

export default ChallengeDetail;
